import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { BarController, BarElement, CategoryScale, ChartData, Chart as ChartJS, LinearScale, Tooltip } from 'chart.js'
import { useTranslation } from 'react-i18next'
import { PriceHistoryChartProps, PriceHistoryWrapperProps } from './types.ts'
import { getPriceHistoryChartDatasets } from './chartjs.config.ts'
import { formatPeriodLabel } from './utils.ts'
import PriceHistoryChartDesktop from './ChartDesktop.tsx'
import PriceHistoryChartMobile from './ChartMobile.tsx'
import ChartLayout from 'components/charts/chart-layout/ChartLayout.tsx'
import Legend from 'components/charts/legend/Legend.tsx'
import { LegendProps } from 'components/charts/legend/types.ts'
import { yScaleTitle } from 'components/charts/plugins.ts'
import { DSChartColors } from 'types/colors.ts'
import { formatAmount } from 'utils/format.ts'
import { Granularity } from 'types/contracts.ts'
import { GRANULARITY_DURATION } from 'constants/datetime.ts'

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, Tooltip, yScaleTitle)

const PriceHistoryChart = ({ data, energyType, granularity, isFetching, isTablet, layoutClassName }: PriceHistoryWrapperProps) => {
  // i18n
  const { t } = useTranslation(['consumption', 'marketPrices', 'common'])

  // Local state
  const [activeDataPointIndex, setActiveDataPointIndex] = useState<number | null>(null)

  // Memoized constants
  const labels = useMemo<string[]>(() => data.map(({ date }) => dayjs(date).toISOString()), [data])

  const chartData = useMemo<ChartData<'bar', (number | null)[]>>(
    () => ({
      labels,
      datasets: getPriceHistoryChartDatasets({ labels, data, granularity, isMobile: isTablet })
    }),
    [labels, data, granularity, isTablet]
  )

  const chartProps: PriceHistoryChartProps = {
    chartData,
    granularity,
    isFetching,
    labels,
    setHoverDataPointIndex: setActiveDataPointIndex
  }

  // The price of the hovered period, or the average of every period that already has a price
  const hoveredPeriod = typeof activeDataPointIndex === 'number' ? data[activeDataPointIndex] : undefined
  const knownPrices = data.map(({ price }) => price).filter((price): price is number => price !== null)
  const price = hoveredPeriod
    ? hoveredPeriod.price
    : knownPrices.length
      ? knownPrices.reduce((total, current) => total + current, 0) / knownPrices.length
      : undefined
  const today = dayjs()

  /**
   * A month that is still running has no final price yet, so it gets a subdued indicator
   * @param {string} date
   * @returns {boolean}
   */
  const isRunningPeriod = (date: string): boolean => {
    return granularity === Granularity.MONTH && today.isSame(date, GRANULARITY_DURATION[Granularity.MONTH].unit)
  }

  // A hovered period only shows its own indicator, the average shows every indicator present in the data
  const legendIndicators: LegendProps['indicator'] = hoveredPeriod
    ? [{ color: isRunningPeriod(hoveredPeriod.date) ? DSChartColors.GREY_SUBDUED : DSChartColors.GREY, full: true }]
    : [
        { color: DSChartColors.GREY, full: true },
        data.some(({ date }) => isRunningPeriod(date)) && { color: DSChartColors.GREY_SUBDUED, full: true }
      ]

  // Reset active data point index if data is not available
  useEffect(() => {
    if (isFetching || (typeof activeDataPointIndex === 'number' && !data[activeDataPointIndex])) {
      setActiveDataPointIndex(null)
    }
  }, [activeDataPointIndex, data, isFetching])

  return (
    <ChartLayout className={layoutClassName}>
      {/* CHART */}
      {isTablet ? <PriceHistoryChartMobile {...chartProps} /> : <PriceHistoryChartDesktop {...chartProps} />}

      {/* LEGEND */}
      <Legend boxed>
        <Legend.Entry
          tooltip={t(($) => $.chart.totals.price.tooltip[energyType])}
          isLoading={isFetching}
          label={
            hoveredPeriod
              ? formatPeriodLabel(hoveredPeriod.date, granularity)
              : t(($) => $.totals.averagePrice, {
                  ns: 'marketPrices'
                })
          }
          indicator={activeDataPointIndex === null || typeof hoveredPeriod?.price === 'number' ? legendIndicators : undefined}
        >
          {typeof price === 'number' && (
            <data value={price}>
              {t(($) => $.units.currencyPerKwh, {
                value: formatAmount(price, { maxFractionDigits: 3, minFractionDigits: 0 }),
                ns: 'common'
              })}
            </data>
          )}
        </Legend.Entry>
      </Legend>
    </ChartLayout>
  )
}

export default PriceHistoryChart
